/**
 * @fileoverview Single source of truth for mobile number and email format rules.
 * @description Holds the patterns and input attributes every contact field in the app relies on —
 * the Zod field builders in contact-fields.schema.ts validate against these, and every mobile
 * number input spreads MOBILE_NUMBER_INPUT_PROPS so the browser enforces the same length cap the
 * schema checks and strips anything that isn't a digit as it's typed or pasted. Changing a rule
 * here changes it for every form at once.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { keepMobileNumberDigits, pasteMobileNumberDigits } from './contact-fields.utils'

/** Number of digits in a mobile number (Indian format, no country code). */
export const MOBILE_NUMBER_LENGTH = 10

/** Exactly 10 digits, starting 6–9 — the valid range for Indian mobile numbers. */
export const MOBILE_NUMBER_PATTERN = /^[6-9]\d{9}$/

/**
 * Attributes to spread onto every mobile number input: caps typing at 10 characters, opens the
 * numeric keypad on mobile devices, and drops any non-digit as it's typed (onInput runs before the
 * form library's own onChange, so the form only ever sees digits) or pasted (onPaste cleans the
 * clipboard text before the browser's maxLength can cut a "+91 …" number short). Spread it before
 * `register(...)` — register supplies onChange, not onInput/onPaste, so none override each other.
 */
export const MOBILE_NUMBER_INPUT_PROPS = {
  type: 'tel',
  inputMode: 'numeric',
  autoComplete: 'tel',
  maxLength: MOBILE_NUMBER_LENGTH,
  onInput: keepMobileNumberDigits,
  onPaste: pasteMobileNumberDigits,
} as const
